import { SignalWatcher, WithDisposable } from '@blocksuite/affine/global/lit';
import { unsafeCSSVarV2 } from '@blocksuite/affine/shared/theme';
import { ShadowlessElement } from '@blocksuite/affine/std';
import { CloseIcon, PlusIcon, ToggleRightIcon } from '@blocksuite/icons/lit';
import { css, html, type TemplateResult, unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';

import { contextChipThemeVars } from './context-chip-style';
import type { ChipState } from './type';

export class ChatPanelChip extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = css`
    .chip-card {
      ${unsafeCSS(contextChipThemeVars)}
      display: flex;
      height: var(--ai-context-chip-height);
      align-items: center;
      justify-content: center;
      padding: 0 var(--ai-context-chip-padding-x);
      border-radius: var(--ai-context-chip-radius);
      border: 0.5px solid var(--ai-context-chip-border);
      background: var(--ai-context-chip-bg);
      box-sizing: border-box;
      color: var(--ai-context-chip-color);
      user-select: none;
    }

    .chip-card[data-expanded='true'][data-expand-direction='down']
      .chip-card-chevron {
      transform: rotate(90deg);
    }

    .chip-card[data-expanded='true'][data-expand-direction='up']
      .chip-card-chevron {
      transform: rotate(-90deg);
    }

    .chip-card[data-state='candidate'] {
      border-width: 1px;
      border-style: dashed;
      border-color: ${unsafeCSSVarV2('icon/tertiary')};
      background: ${unsafeCSSVarV2('layer/background/secondary')};
      color: ${unsafeCSSVarV2('icon/secondary')};
    }
    .chip-card[data-state='candidate']:hover {
      background: ${unsafeCSSVarV2('layer/background/hoverOverlay')};
    }
    .chip-card[data-state='candidate'] svg {
      color: ${unsafeCSSVarV2('icon/secondary')};
    }
    .chip-card[data-state='failed'] {
      color: ${unsafeCSSVarV2('status/error')};
      background: ${unsafeCSSVarV2('layer/background/error')};
    }
    .chip-card-content {
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 0;
    }
    .chip-card[data-state='failed'] svg {
      color: ${unsafeCSSVarV2('status/error')};
    }
    .chip-card svg {
      width: 16px;
      height: 16px;
      display: block;
      flex-shrink: 0;
      color: var(--ai-context-chip-icon);
    }
    .chip-card-title {
      display: inline-block;
      margin: 0 6px;
      font-size: 12px;
      line-height: 18px;
      min-width: 16px;
      max-width: 176px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .chip-card[data-expandable='true'] {
      cursor: pointer;
    }
    .chip-card[data-expandable='true']:hover {
      background: var(--ai-context-chip-bg-hover);
    }
    .chip-card[data-state='candidate'] {
      cursor: pointer;
    }
    .chip-card-chevron {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      margin-right: -2px;
      color: var(--ai-context-chip-icon-secondary);
      transform: rotate(0deg);
      transition: transform 120ms ease;
    }
    .chip-card-chevron svg {
      width: 14px;
      height: 14px;
    }
    .chip-card-close {
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      border-radius: 4px;
      flex-shrink: 0;
    }
    .chip-card-close:hover {
      background: var(--ai-context-chip-bg-hover);
    }
  `;

  @property({ attribute: false })
  accessor state!: ChipState;

  @property({ attribute: false })
  accessor name!: string;

  @property({ attribute: false })
  accessor tooltip!: string;

  @property({ attribute: false })
  accessor icon!: TemplateResult<1>;

  @property({ attribute: false })
  accessor closeable: boolean = false;

  @property({ attribute: false })
  accessor expandable: boolean = false;

  @property({ attribute: false })
  accessor expanded: boolean = false;

  @property({ attribute: false })
  accessor expandDirection: 'up' | 'down' = 'down';

  @property({ attribute: false })
  accessor onChipDelete: () => void = () => {};

  @property({ attribute: false })
  accessor onChipClick: () => void = () => {};

  override render() {
    const isCandidate = this.state === 'candidate';
    return html`
      <div
        class="chip-card"
        data-testid="chat-panel-chip"
        data-state=${this.state}
        data-expandable=${this.expandable}
        data-expanded=${this.expandable && !isCandidate && this.expanded}
        data-expand-direction=${this.expandDirection}
        @click=${this.onChipClick}
      >
        ${this.expandable && !isCandidate
          ? html`<span class="chip-card-chevron" aria-hidden="true"
              >${ToggleRightIcon({ width: '14px', height: '14px' })}</span
            >`
          : ''}
        <div class="chip-card-content">
          ${this.icon}
          <span class="chip-card-title">
            <span data-testid="chat-panel-chip-title">${this.name}</span>
          </span>
          <affine-tooltip>${this.tooltip}</affine-tooltip>
        </div>
        ${isCandidate
          ? html`${PlusIcon()}`
          : this.closeable
            ? html`
                <div
                  class="chip-card-close"
                  @click=${(event: MouseEvent) => {
                    event.preventDefault();
                    event.stopPropagation();
                    this.onChipDelete();
                  }}
                >
                  ${CloseIcon()}
                </div>
              `
            : ''}
      </div>
    `;
  }
}
